import { SYSTEM_TYPE } from '@/app/store/systemStore'
import { buildReceiverHostUrl } from '@/shared/lib/buildReceiverHostUrl'
import axios from 'axios'

export interface SourceApiResponse<T> {
  data: T[]
  error?: string
  status?: number
  rawMessage?: string
}

export interface GlonassoftSourceCredentials {
  login: string
  password: string
  agentId?: string
}

export const loadSourceData = async <T>(
  endpoint: string,
  token: string,
  sourceApi: string,
  systemUrl: string,
  system?: string | null,
  signal?: AbortSignal
): Promise<SourceApiResponse<T>> => {
  try {
    const isOmnicomm = system === SYSTEM_TYPE.OMNICOMM
    const isGlonassoft = system === SYSTEM_TYPE.GLONASSOFT

    if (isOmnicomm || isGlonassoft) {
      const url = `${sourceApi}/public/${endpoint}?auth=${encodeURIComponent(token)}&host=${systemUrl}&getImported=true`

      const response = await axios.get<T[]>(url, { signal })
      // console.log(response.data)

      return {
        data: response.data || [],
      }
    } else {
      const url = `${sourceApi}/public/${endpoint}?auth=${token}&host=${systemUrl}&getImported=true`
      // console.log(url)

      const response = await axios.get<T[]>(url, { signal })
      // console.log(response.data)

      return {
        data: response.data || [],
      }
    }
  } catch (error) {
    console.error(`❌ Ошибка загрузки ${endpoint}:`, error)

    if (axios.isAxiosError(error)) {
      console.error('🔴 Детали ошибки:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      })
    }

    return {
      data: [],
      error: `Не удалось загрузить ${getEndpointName(endpoint)}`,
      ...extractAxiosErrorDetails(error),
    }
  }
}

const DEFAULT_TIMEOUT = 120_000

export const loadGlonassoftData = async <T>(
  endpoint: string,
  credentials: GlonassoftSourceCredentials,
  sourceApi: string,
  systemUrl: string,
  signal?: AbortSignal,
  timeout = DEFAULT_TIMEOUT
): Promise<SourceApiResponse<T>> => {
  try {
    const url = `${sourceApi}/public/${endpoint}?auth=${encodeURIComponent(JSON.stringify(credentials))}&host=${buildReceiverHostUrl(systemUrl)}&getImported=true`

    const response = await axios.get<T[]>(url, { signal, timeout })

    return {
      data: response.data || [],
    }
  } catch (error) {
    console.error(`❌ Ошибка загрузки ${endpoint}:`, error)

    if (axios.isAxiosError(error)) {
      console.error('🔴 Детали ошибки:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      })
    }

    return {
      data: [],
      error: `Не удалось загрузить ${getEndpointName(endpoint)}`,
      ...extractAxiosErrorDetails(error),
    }
  }
}

export const getEndpointName = (endpoint: string): string => {
  const endpointNames: Record<string, string> = {
    Agents: 'учетные записи',
    Vehicles: 'объекты',
    Drivers: 'водителей',
    Users: 'пользователей',
    Geozones: 'геозоны',
    Retranslations: 'ретрансляторы',
  }

  return endpointNames[endpoint] || endpoint.toLowerCase()
}

const extractAxiosErrorDetails = (
  error: unknown
): { status?: number; rawMessage?: string } => {
  if (!axios.isAxiosError(error)) return {}

  const data = error.response?.data
  const rawMessage =
    typeof data === 'string' ? data : (data as { message?: string })?.message

  return { status: error.response?.status, rawMessage }
}
